import greyDialogTitleSx from '../Dialog/greyDialogTitleSx';
import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Box,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Typography,
    Alert,
    Checkbox
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { UserPermissions } from '../../services/PermissionService';

const ALL = '__all__';

interface SubModule {
    id: string;
    name: string;
    route?: string;
    permissions: string[];
}

interface Module {
    id: string;
    name: string;
    description: string;
    permissions: string[];
    subModules?: SubModule[];
}

interface Scope {
    id: string;
    name: string;
    description: string;
}

interface User {
    _id: string;
    name: string;
    email: string;
}

interface ModuleAccessDialogProps {
    open: boolean;
    onClose: () => void;
    selectedUser: User | null;
    onSave: (data: any) => Promise<void>;
    availableModules: Module[];
    availableScopes: Scope[];
    /** What the user already has, so the form opens pre-filled instead of blank. */
    existingPermissions?: UserPermissions | null;
}

interface ModulePermission {
    module: string;
    subModule?: string | null;
    permissions: string[];
    scope: string;
}

interface ContextPermissions {
    isManager: boolean;
    canApproveLeaves: boolean;
    canApproveAttendance: boolean;
    canApproveTimesheets: boolean;
    canApproveExpenses: boolean;
    approvalScope: string;
    canAssignRoles: boolean;
    maxRoleLevel: number;
}

const ModuleAccessDialog: React.FC<ModuleAccessDialogProps> = ({
    open,
    onClose,
    selectedUser,
    onSave,
    availableModules,
    availableScopes,
    existingPermissions
}) => {
    const [selectedModule, setSelectedModule] = useState<string>('');
    // Empty = the whole module; otherwise one or more specific sub-module ids.
    const [selectedSubModules, setSelectedSubModules] = useState<string[]>([]);
    const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
    const [selectedScope, setSelectedScope] = useState<string>('self');
    const [currentModules, setCurrentModules] = useState<ModulePermission[]>([]);
    const [contextPermissions, setContextPermissions] = useState<ContextPermissions>({
        isManager: false,
        canApproveLeaves: false,
        canApproveAttendance: false,
        canApproveTimesheets: false,
        canApproveExpenses: false,
        approvalScope: 'subordinates',
        canAssignRoles: false,
        maxRoleLevel: 0
    });
    const [reason, setReason] = useState<string>('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>('');

    // Snapshot of the manager/approval flags when the dialog opened, so Save only
    // writes them when one actually moved.
    const [initialContext, setInitialContext] = useState<ContextPermissions | null>(null);

    const DEFAULT_CONTEXT: ContextPermissions = {
        isManager: false,
        canApproveLeaves: false,
        canApproveAttendance: false,
        canApproveTimesheets: false,
        canApproveExpenses: false,
        approvalScope: 'subordinates',
        canAssignRoles: false,
        maxRoleLevel: 0
    };

    // Pre-fill the manager/approval flags from what the user already has, so the
    // toggles reflect reality instead of always opening off. Module grants are
    // NOT seeded here — they are already listed in the Module Access tab behind
    // this dialog; this list stays a "what am I adding now" list.
    useEffect(() => {
        if (!open) return;

        const seededContext: ContextPermissions = {
            ...DEFAULT_CONTEXT,
            ...(existingPermissions?.contextPermissions as Partial<ContextPermissions> || {})
        };
        setContextPermissions(seededContext);
        setInitialContext(seededContext);

        setCurrentModules([]);
        setSelectedModule('');
        setSelectedSubModules([]);
        setSelectedPermissions([]);
        setSelectedScope('self');
        setReason('');
        setError('');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, existingPermissions]);

    const contextChanged =
        !initialContext || JSON.stringify(initialContext) !== JSON.stringify(contextPermissions);

    const hasChanges = contextChanged || currentModules.length > 0;

    const handleModuleChange = (event: SelectChangeEvent<string>) => {
        setSelectedModule(event.target.value);
        // Changing the module invalidates the chosen sub-modules and permissions.
        setSelectedSubModules([]);
        setSelectedPermissions([]);
    };

    const handleSubModuleChange = (event: SelectChangeEvent<string[]>) => {
        const value = event.target.value;
        const list = typeof value === 'string' ? value.split(',') : value;
        // "All" = whole module (empty selection), which also covers future sub-modules.
        setSelectedSubModules(list.includes(ALL) ? [] : list);
        // The action set can differ per sub-module, so clear the selection.
        setSelectedPermissions([]);
    };

    const handlePermissionChange = (event: SelectChangeEvent<string[]>) => {
        const value = event.target.value;
        const list = typeof value === 'string' ? value.split(',') : value;
        if (list.includes(ALL)) {
            setSelectedPermissions(selectedPermissions.length === availablePermissions.length ? [] : [...availablePermissions]);
            return;
        }
        setSelectedPermissions(list);
    };

    const handleScopeChange = (event: SelectChangeEvent<string>) => {
        setSelectedScope(event.target.value);
    };

    // A grant line is unique per (module, sub-module) pair.
    const sameGrant = (a: ModulePermission, b: ModulePermission) =>
        a.module === b.module && (a.subModule || null) === (b.subModule || null);

    const handleAddModule = () => {
        if (!selectedModule || selectedPermissions.length === 0) {
            setError('Please select a module and at least one permission');
            return;
        }

        // Empty sub-module selection = one whole-module grant; otherwise one grant
        // per selected sub-module (same permissions + scope applied to each).
        const targets: (string | null)[] = selectedSubModules.length > 0 ? selectedSubModules : [null];
        const updated = [...currentModules];
        for (const sub of targets) {
            const newModule: ModulePermission = {
                module: selectedModule,
                subModule: sub,
                permissions: selectedPermissions,
                scope: selectedScope
            };
            const existingIndex = updated.findIndex(m => sameGrant(m, newModule));
            if (existingIndex >= 0) {
                updated[existingIndex] = newModule;
            } else {
                updated.push(newModule);
            }
        }
        setCurrentModules(updated);

        // Reset form
        setSelectedModule('');
        setSelectedSubModules([]);
        setSelectedPermissions([]);
        setSelectedScope('self');
        setError('');
    };

    const handleRemoveModule = (target: ModulePermission) => {
        setCurrentModules(currentModules.filter(m => !sameGrant(m, target)));
    };

    const handleSave = async () => {
        setLoading(true);
        setError('');

        try {
            // Save module permissions
            for (const moduleData of currentModules) {
                await onSave({
                    type: 'module',
                    userId: selectedUser?._id,
                    data: moduleData,
                    reason
                });
            }

            // Save context permissions only when they actually changed.
            if (contextChanged) {
                await onSave({
                    type: 'context',
                    userId: selectedUser?._id,
                    data: contextPermissions,
                    reason
                });
            }

            onClose();
            resetForm();
        } catch (err: any) {
            setError(err.message || 'Failed to save permissions');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setSelectedModule('');
        setSelectedSubModules([]);
        setSelectedPermissions([]);
        setSelectedScope('self');
        setCurrentModules([]);
        setContextPermissions({ ...DEFAULT_CONTEXT });
        setInitialContext(null);
        setReason('');
        setError('');
    };

    const getModuleName = (moduleId: string) => {
        const module = availableModules.find(m => m.id === moduleId);
        return module ? module.name : moduleId;
    };

    const getScopeName = (scopeId: string) => {
        const scope = availableScopes.find(s => s.id === scopeId);
        return scope ? scope.name : scopeId;
    };

    const getSubModuleName = (moduleId: string, subModuleId?: string | null) => {
        if (!subModuleId) return '';
        const module = availableModules.find(m => m.id === moduleId);
        const sub = module?.subModules?.find(s => s.id === subModuleId);
        return sub ? sub.name : subModuleId;
    };

    // What the user already has, so the form only offers what is still missing.
    const heldPermissions = (moduleId: string, subModuleId?: string | null): string[] => {
        const access: any = existingPermissions?.moduleAccess?.[moduleId];
        if (!access) return [];
        // A whole-module grant already covers every sub-module.
        return subModuleId
            ? [...(access.permissions || []), ...(access.subModules?.[subModuleId]?.permissions || [])]
            : access.permissions || [];
    };

    const moduleFullyGranted = (module: Module) => {
        const held = heldPermissions(module.id);
        const wholeGranted = module.permissions.every(p => held.includes(p));
        const subsGranted = (module.subModules || []).every(sub => {
            const subHeld = heldPermissions(module.id, sub.id);
            return sub.permissions.every(p => subHeld.includes(p));
        });
        return wholeGranted && subsGranted;
    };

    const moduleOptions = availableModules.filter(m => !moduleFullyGranted(m));
    const selectedModuleData = availableModules.find(m => m.id === selectedModule);
    const subModuleOptions = (selectedModuleData?.subModules || []).filter(sub => {
        const held = heldPermissions(selectedModule, sub.id);
        return !sub.permissions.every(p => held.includes(p));
    });
    const chosenSubs = subModuleOptions.filter(s => selectedSubModules.includes(s.id));
    // Permissions offered: when sub-modules are picked, only actions valid for
    // ALL of them (intersection), so a bulk grant is always valid per sub-module.
    // With none picked, the whole-module actions apply.
    // Permissions the user already holds everywhere the grant would apply are dropped.
    const availablePermissions: string[] = (chosenSubs.length > 0
        ? chosenSubs.reduce<string[]>(
            (acc, s, i) => (i === 0 ? [...s.permissions] : acc.filter(p => s.permissions.includes(p))),
            []
          )
        : (selectedModuleData?.permissions || [])
    ).filter(p => chosenSubs.length > 0
        ? !chosenSubs.every(s => heldPermissions(selectedModule, s.id).includes(p))
        : !heldPermissions(selectedModule).includes(p));

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                style: { minHeight: '600px' }
            }}
        >
            <DialogTitle sx={greyDialogTitleSx}>
                Manage Module Access - {selectedUser?.name}
                <Typography variant="body2" color="textSecondary">
                    {selectedUser?.email}
                </Typography>
            </DialogTitle>

            <DialogContent dividers>
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}

                <Box mb={3}>
                    <Typography variant="h6" gutterBottom>
                        Module Permissions
                    </Typography>
                    
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Module</InputLabel>
                                <Select
                                    value={selectedModule}
                                    onChange={handleModuleChange}
                                    label="Module"
                                >
                                    {moduleOptions.map((module) => (
                                        <MenuItem key={module.id} value={module.id}>
                                            {module.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small" disabled={!selectedModule || subModuleOptions.length === 0}>
                                <InputLabel shrink>Sub-module</InputLabel>
                                <Select
                                    multiple
                                    displayEmpty
                                    notched={true}
                                    value={selectedSubModules}
                                    onChange={handleSubModuleChange}
                                    label="Sub-module"
                                    renderValue={(selected) =>
                                        selected.length === 0 ? (
                                            <em style={{ color: '#8a8a8a' }}>All (whole module)</em>
                                        ) : (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {selected.map((id) => (
                                                    <Chip key={id} label={getSubModuleName(selectedModule, id)} size="small" />
                                                ))}
                                            </Box>
                                        )
                                    }
                                >
                                    <MenuItem value={ALL} sx={{ py: 0.25 }}>
                                        <Checkbox size="small" sx={{ p: 0.5, mr: 1 }} checked={selectedSubModules.length === 0} />
                                        <span>All (whole module)</span>
                                    </MenuItem>
                                    {subModuleOptions.map((sub) => (
                                        <MenuItem key={sub.id} value={sub.id} sx={{ py: 0.25 }}>
                                            <Checkbox size="small" sx={{ p: 0.5, mr: 1 }} checked={selectedSubModules.includes(sub.id)} />
                                            <span>{sub.name}</span>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <FormControl fullWidth size="small">
                                <InputLabel>Scope</InputLabel>
                                <Select
                                    value={selectedScope}
                                    onChange={handleScopeChange}
                                    label="Scope"
                                >
                                    {availableScopes.map((scope) => (
                                        <MenuItem key={scope.id} value={scope.id}>
                                            {scope.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={10}>
                            <FormControl fullWidth size="small" disabled={!selectedModule}>
                                <InputLabel>Permissions</InputLabel>
                                <Select
                                    multiple
                                    value={selectedPermissions}
                                    onChange={handlePermissionChange}
                                    label="Permissions"
                                    renderValue={(selected) => (
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                            {selected.map((value) => (
                                                <Chip key={value} label={value} size="small" />
                                            ))}
                                        </Box>
                                    )}
                                >
                                    {availablePermissions.length > 0 && (
                                        <MenuItem value={ALL} sx={{ py: 0.25 }}>
                                            <Checkbox
                                                size="small"
                                                sx={{ p: 0.5, mr: 1 }}
                                                checked={selectedPermissions.length === availablePermissions.length}
                                                indeterminate={selectedPermissions.length > 0 && selectedPermissions.length < availablePermissions.length}
                                            />
                                            <span>All</span>
                                        </MenuItem>
                                    )}
                                    {availablePermissions.map((permission) => (
                                        <MenuItem key={permission} value={permission} sx={{ py: 0.25 }}>
                                            <Checkbox size="small" sx={{ p: 0.5, mr: 1 }} checked={selectedPermissions.includes(permission)} />
                                            <span>{permission}</span>
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={2}>
                            <Button
                                variant="contained"
                                onClick={handleAddModule}
                                disabled={!selectedModule || selectedPermissions.length === 0}
                                fullWidth
                                size="small"
                            >
                                Add
                            </Button>
                        </Grid>
                    </Grid>

                    {selectedModuleData && (
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                            {selectedModuleData.description}
                        </Typography>
                    )}
                </Box>

                {currentModules.length > 0 && (
                    <Box mb={3}>
                        <Typography variant="subtitle1" gutterBottom>
                            Current Module Access:
                        </Typography>
                        {currentModules.map((module, index) => (
                            <Box key={index} sx={{ mb: 1, p: 1, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                    <Box>
                                        <Typography variant="body2" fontWeight="bold">
                                            {getModuleName(module.module)}
                                            {module.subModule ? ` › ${getSubModuleName(module.module, module.subModule)}` : ''} ({getScopeName(module.scope)})
                                        </Typography>
                                        <Box sx={{ mt: 0.5 }}>
                                            {module.permissions.map((permission) => (
                                                <Chip
                                                    key={permission}
                                                    label={permission}
                                                    size="small"
                                                    sx={{ mr: 0.5, mb: 0.5 }}
                                                />
                                            ))}
                                        </Box>
                                    </Box>
                                    <Button
                                        size="small"
                                        color="error"
                                        onClick={() => handleRemoveModule(module)}
                                    >
                                        Remove
                                    </Button>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}

                <Box>
                    <Typography variant="subtitle1" gutterBottom>
                        Reason for Changes (Optional)
                    </Typography>
                    <textarea
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        placeholder="Enter reason for permission changes..."
                        style={{
                            width: '100%',
                            minHeight: '60px',
                            padding: '8px',
                            border: '1px solid #ccc',
                            borderRadius: '4px',
                            fontFamily: 'inherit'
                        }}
                    />
                </Box>
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose} disabled={loading}>
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={loading || !hasChanges}
                >
                    {loading ? 'Saving...' : 'Save Permissions'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ModuleAccessDialog;